import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Employee, OnboardingProgress } from '../../types';
import WizardStepper from './WizardStepper';
import BasicDetailsStep from './steps/BasicDetailsStep';
import BackgroundSearchStep from './steps/BackgroundSearchStep';
import DocumentGenerationStep from './steps/DocumentGenerationStep';
import DocumentPackageStep from './steps/DocumentPackageStep';
import SignatureVerificationStep from './steps/SignatureVerificationStep';
import PayrollIntegrationStep from './steps/PayrollIntegrationStep';
import I9CompletionStep from './steps/I9CompletionStep';
import { WIZARD_STEPS, type WizardStepKey, type WizardDraftData } from './types';
import { saveDraftToStorage, loadDraftFromStorage, clearDraftFromStorage, hasDraft } from './wizardUtils';
import { onboardingApi, employeeApi, docusignApi } from '../../services/hrApi';
import './OnboardingWizard.scss';

interface OnboardingWizardProps {
  draftEmployeeId?: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  draftEmployeeId,
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStepKey>('basic_details');
  const [completedSteps, setCompletedSteps] = useState<WizardStepKey[]>([]);
  const [employeeId, setEmployeeId] = useState<string | undefined>(draftEmployeeId);
  const [stepData, setStepData] = useState<Record<WizardStepKey, any>>({} as Record<WizardStepKey, any>);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const lastCompletedStatusRef = useRef<Record<WizardStepKey, boolean>>({} as Record<WizardStepKey, boolean>);
  const backendCompletedStepsRef = useRef<WizardStepKey[]>([]);

  // Load draft or existing employee data on mount
  useEffect(() => {
    // If draftEmployeeId is provided, we're opening an existing employee's onboarding
    // Load their data directly from backend
    if (draftEmployeeId) {
      loadDraftData();
    } else {
      // "Onboard New Employee" - always start fresh so we never show another employee's data
      // (e.g. user opened an existing employee, went back, deleted them, then clicked Onboard New Employee)
      clearDraftFromStorage();
      setCurrentStep('basic_details');
      setCompletedSteps([]);
      setStepData({} as Record<WizardStepKey, any>);
      setEmployeeId(undefined);
      lastCompletedStatusRef.current = {} as Record<WizardStepKey, boolean>;
      backendCompletedStepsRef.current = [];
    }
  }, [draftEmployeeId]);

  const loadDraftData = async () => {
    setLoading(true);
    try {
      // If draftEmployeeId is provided, it means we're opening an existing employee's onboarding
      // Don't load from localStorage in this case, load from backend
      if (draftEmployeeId) {
        setEmployeeId(draftEmployeeId);
        
        // Load employee data from backend
        try {
          const employee = await employeeApi.getEmployee(draftEmployeeId);
          
          // Populate basic_details step with employee data
          setStepData((prev) => ({
            ...prev,
            basic_details: {
              first_name: employee.first_name || '',
              last_name: employee.last_name || '',
              email: employee.email || '',
              phone: employee.phone || '',
              date_of_birth: employee.date_of_birth || '',
              hire_date: employee.hire_date || new Date().toISOString().split('T')[0],
              department: employee.department || '',
              position: employee.position || '',
              employee_type: employee.employee_type || '',
              classification: employee.classification || '',
              salary: employee.salary !== undefined ? String(employee.salary) : '',
              salary_rate: employee.salary_rate || '',
              address: employee.address || '',
            },
          }));
        } catch (error) {
          console.error('Error loading employee data:', error);
        }
        
        // Load onboarding progress from backend
        try {
          const progress = await onboardingApi.getOnboardingProgressByEmployee(draftEmployeeId);
          if (Array.isArray(progress)) {
            const completed = progress
              .filter((p: OnboardingProgress) => p.completed)
              .map((p: OnboardingProgress) => p.step_name as WizardStepKey);
            setCompletedSteps(completed);
            backendCompletedStepsRef.current = completed;
            
            // Initialize lastCompletedStatusRef with backend completion status
            // This prevents unnecessary saves when step components detect already-completed steps
            completed.forEach((step) => {
              lastCompletedStatusRef.current[step] = true;
            });
          }
        } catch (error) {
          console.error('Error loading onboarding progress:', error);
        }

        // Check DocuSign envelope status and update package_sent in stepData
        try {
          // First check if DocuSign is connected with a valid token
          const docusignStatus = await docusignApi.checkStatus();
          if (docusignStatus.connected && docusignStatus.valid) {
            // Check for existing envelope
            const envelopeResult = await docusignApi.getEnvelopeDraft(draftEmployeeId, 'employee-onboarding');
            if (envelopeResult.exists) {
              const envelopeStatus = envelopeResult.status || 'created';
              // Update package_sent based on envelope status
              const packageSent = envelopeStatus === 'sent' || envelopeStatus === 'completed';
              setStepData((prev) => ({
                ...prev,
                document_package: {
                  ...prev.document_package,
                  package_sent: packageSent,
                  recipient_email: prev.document_package?.recipient_email || '',
                  sent_date: prev.document_package?.sent_date || '',
                  documentsForSigning: prev.document_package?.documentsForSigning || [],
                },
              }));
            } else {
              // No envelope exists, ensure package_sent is false
              setStepData((prev) => ({
                ...prev,
                document_package: {
                  ...prev.document_package,
                  package_sent: false,
                  recipient_email: prev.document_package?.recipient_email || '',
                  sent_date: prev.document_package?.sent_date || '',
                  documentsForSigning: prev.document_package?.documentsForSigning || [],
                },
              }));
            }
          }
        } catch (error) {
          console.error('Error checking DocuSign envelope status:', error);
          // On error, don't update package_sent - let the step component handle it
        }
      } else {
        // No draftEmployeeId - check localStorage for unsaved draft
        const draft = loadDraftFromStorage();
        if (draft) {
          setEmployeeId(draft.employeeId);
          setStepData(draft.stepData || ({} as Record<WizardStepKey, any>));
          setCompletedSteps(draft.completedSteps || []);
          setCurrentStep(draft.currentStep || 'basic_details');
          
          // If employeeId exists in draft, also load progress from backend
          if (draft.employeeId) {
            try {
              const progress = await onboardingApi.getOnboardingProgressByEmployee(draft.employeeId);
              if (Array.isArray(progress)) {
                const completed = progress
                  .filter((p: OnboardingProgress) => p.completed)
                  .map((p: OnboardingProgress) => p.step_name as WizardStepKey);
                setCompletedSteps(completed);
                backendCompletedStepsRef.current = completed;
                
                // Initialize lastCompletedStatusRef with backend completion status
                // This prevents unnecessary saves when step components detect already-completed steps
                completed.forEach((step) => {
                  lastCompletedStatusRef.current[step] = true;
                });
              }
            } catch (error) {
              console.error('Error loading onboarding progress:', error);
            }

            // Check DocuSign envelope status and update package_sent in stepData
            try {
              // First check if DocuSign is connected with a valid token
              const docusignStatus = await docusignApi.checkStatus();
              if (docusignStatus.connected && docusignStatus.valid) {
                // Check for existing envelope
                const envelopeResult = await docusignApi.getEnvelopeDraft(draft.employeeId, 'employee-onboarding');
                if (envelopeResult.exists) {
                  const envelopeStatus = envelopeResult.status || 'created';
                  // Update package_sent based on envelope status
                  const packageSent = envelopeStatus === 'sent' || envelopeStatus === 'completed';
                  setStepData((prev) => ({
                    ...prev,
                    document_package: {
                      ...prev.document_package,
                      package_sent: packageSent,
                      recipient_email: prev.document_package?.recipient_email || '',
                      sent_date: prev.document_package?.sent_date || '',
                      documentsForSigning: prev.document_package?.documentsForSigning || [],
                    },
                  }));
                } else {
                  // No envelope exists, ensure package_sent is false
                  setStepData((prev) => ({
                    ...prev,
                    document_package: {
                      ...prev.document_package,
                      package_sent: false,
                      recipient_email: prev.document_package?.recipient_email || '',
                      sent_date: prev.document_package?.sent_date || '',
                      documentsForSigning: prev.document_package?.documentsForSigning || [],
                    },
                  }));
                }
              }
            } catch (error) {
              console.error('Error checking DocuSign envelope status:', error);
              // On error, don't update package_sent - let the step component handle it
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading draft data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save draft data to localStorage (not backend - step completions are saved immediately)
  const saveDraftToLocal = useCallback(() => {
    const draftData: WizardDraftData = {
      employeeId,
      stepData,
      completedSteps,
      currentStep,
      updatedAt: new Date().toISOString(),
    };
    saveDraftToStorage(draftData);
    setHasUnsavedChanges(false);
  }, [employeeId, stepData, completedSteps, currentStep]);

  // Auto-save form data to localStorage periodically (as safety net)
  // Use a ref to avoid re-creating the effect when saveDraftToLocal changes
  const saveDraftToLocalRef = useRef(saveDraftToLocal);
  useEffect(() => {
    saveDraftToLocalRef.current = saveDraftToLocal;
  }, [saveDraftToLocal]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      const timer = setTimeout(() => {
        saveDraftToLocalRef.current();
      }, 3000); // Auto-save form data after 3 seconds of inactivity

      return () => clearTimeout(timer);
    }
  }, [hasUnsavedChanges]);

  const handleStepChange = async (newStep: WizardStepKey) => {
    // Save form data to localStorage when navigating steps
    if (hasUnsavedChanges) {
      saveDraftToLocal();
    }

    // Save current step completion to backend when moving to next step
    if (employeeId && completedSteps.includes(currentStep)) {
      try {
        await onboardingApi.saveOnboardingProgress({
          employee_id: employeeId,
          step_name: currentStep,
          completed: true,
        });
      } catch (error) {
        console.error(`Error saving step completion for ${currentStep}:`, error);
      }
    }

    setCurrentStep(newStep);
    saveDraftToLocal(); // Save current step position to localStorage
  };

  const handleStepDataChange = (step: WizardStepKey, data: any) => {
    setStepData((prev) => {
      const updated = {
        ...prev,
        [step]: { ...prev[step], ...data }, // Merge with existing data to preserve other fields
      };
      
      // If updating document_package and package_sent is set to false, also update step completion
      if (step === 'document_package' && data.package_sent === false) {
        // Remove document_package from completed steps if it's there
        setCompletedSteps((prevCompleted) => {
          if (prevCompleted.includes('document_package')) {
            const updatedCompleted = prevCompleted.filter((s) => s !== 'document_package');
            lastCompletedStatusRef.current['document_package'] = false;
            return updatedCompleted;
          }
          return prevCompleted;
        });
      }
      
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  const handleStepComplete = useCallback((step: WizardStepKey, isComplete: boolean) => {
    // Track previous completion status to detect changes
    const wasComplete = lastCompletedStatusRef.current[step] === true;
    
    // Always update the ref to track completion status
    lastCompletedStatusRef.current[step] = isComplete;
    
    // Don't remove steps that are marked as complete in the backend
    // This prevents step components from accidentally removing completion status
    // when they mount and haven't loaded their data yet
    if (!isComplete && backendCompletedStepsRef.current.includes(step)) {
      return;
    }
    
    // Save to backend immediately when a step is marked as complete
    // This ensures progress is saved even if user doesn't navigate away from the step
    if (isComplete && !wasComplete && employeeId) {
      // Step just became complete - save to backend immediately
      onboardingApi.saveOnboardingProgress({
        employee_id: employeeId,
        step_name: step,
        completed: true,
      }).catch((error) => {
        console.error(`Error saving step completion for ${step}:`, error);
        // Don't block UI on save error, but log it
      });
    }
    
    // Always update completedSteps array if status changed OR if step is complete but not in array
    // This ensures steps are added even if called multiple times
    setCompletedSteps((prev) => {
      // Protect: don't remove steps that are in backend-completed list
      if (!isComplete && backendCompletedStepsRef.current.includes(step)) {
        return prev;
      }
      
      let updated: WizardStepKey[];
      if (isComplete && !prev.includes(step)) {
        // Step is complete but not in array - add it
        updated = [...prev, step];
      } else if (!isComplete && prev.includes(step)) {
        // Step is incomplete but in array - remove it
        updated = prev.filter((s) => s !== step);
      } else {
        // No change needed
        updated = prev;
      }
      
      // Only save to localStorage if array actually changed
      if (updated !== prev) {
        const draftData: WizardDraftData = {
          employeeId,
          stepData,
          completedSteps: updated,
          currentStep,
          updatedAt: new Date().toISOString(),
        };
        saveDraftToStorage(draftData);
      }
      
      return updated;
    });
  }, [employeeId, stepData, currentStep]);

  const handleEmployeeCreated = (employee: Employee) => {
    setEmployeeId(employee.id);
    // Save employeeId to draft
    const draft = loadDraftFromStorage() || {};
    saveDraftToStorage({
      ...draft,
      employeeId: employee.id,
    } as WizardDraftData);
  };

  const handleWizardComplete = async () => {
    if (employeeId) {
      try {
        // Check if all steps are completed
        await onboardingApi.checkCompletion({ employee_id: employeeId });
        // Clear draft
        clearDraftFromStorage();
        if (onComplete) {
          onComplete();
        }
      } catch (error) {
        console.error('Error completing onboarding:', error);
      }
    }
  };

  // Create stable callback for step completion to avoid re-renders
  const stepCompleteCallbacksRef = useRef<Record<WizardStepKey, (isComplete: boolean) => void>>({} as Record<WizardStepKey, (isComplete: boolean) => void>);
  
  useEffect(() => {
    // Create stable callbacks for each step
    WIZARD_STEPS.forEach((step) => {
      stepCompleteCallbacksRef.current[step.key] = (isComplete: boolean) => {
        handleStepComplete(step.key, isComplete);
      };
    });
  }, [handleStepComplete]);

  // After completedSteps is loaded from backend, ensure they stay marked as complete
  // This fixes the issue where step components might call onComplete(false) before their data loads
  useEffect(() => {
    if (backendCompletedStepsRef.current.length > 0 && !loading) {
      // Use a small delay to ensure step components have mounted and state has updated
      const timer = setTimeout(() => {
        // For each backend-completed step, ensure it's in completedSteps
        backendCompletedStepsRef.current.forEach((step) => {
          setCompletedSteps((prev) => {
            if (!prev.includes(step)) {
              // Step is marked complete in backend but not in local state, add it
              return [...prev, step];
            }
            return prev;
          });
          // Also update the ref to mark it as complete
          lastCompletedStatusRef.current[step] = true;
          // Notify the step component that it's complete
          const callback = stepCompleteCallbacksRef.current[step];
          if (callback) {
            callback(true);
          }
        });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const renderStepContent = () => {
    const stepProps = {
      employeeId,
      data: stepData[currentStep],
      onDataChange: (data: any) => handleStepDataChange(currentStep, data),
      onComplete: stepCompleteCallbacksRef.current[currentStep] || ((isComplete: boolean) => handleStepComplete(currentStep, isComplete)),
      onEmployeeCreated: handleEmployeeCreated,
      onNavigateToStep: (step: WizardStepKey) => handleStepChange(step),
      onUpdateStepData: (step: WizardStepKey, data: any) => handleStepDataChange(step, data),
    };

    switch (currentStep) {
      case 'basic_details':
        return <BasicDetailsStep {...stepProps} />;
      case 'background_search':
        return <BackgroundSearchStep {...stepProps} />;
      case 'document_generation':
        return <DocumentGenerationStep {...stepProps} />;
      case 'document_package':
        return <DocumentPackageStep {...stepProps} />;
      case 'signature_verification':
        return <SignatureVerificationStep {...stepProps} />;
      case 'payroll_integration':
        return <PayrollIntegrationStep {...stepProps} />;
      case 'i9_completion':
        return <I9CompletionStep {...stepProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="wizard-loading">
        <p>Loading onboarding wizard...</p>
      </div>
    );
  }

  const allStepsCompleted = completedSteps.length === WIZARD_STEPS.length;

  return (
    <div className="onboarding-wizard">
      <div className="wizard-container">
        <div className="wizard-header">
          <div>
            <h3>Onboarding Wizard</h3>
            <p className="wizard-description">Follow the steps to onboard a new employee</p>
          </div>
        </div>

        <WizardStepper
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={handleStepChange}
        />

        <div className="wizard-content">
          {renderStepContent()}
        </div>

        <div className="wizard-footer">
          <div className="footer-left">
            {onCancel && (
              <button className="btn-cancel" onClick={onCancel}>
                Cancel
              </button>
            )}
          </div>
          <div className="footer-right">
            {WIZARD_STEPS.findIndex((s) => s.key === currentStep) > 0 && (
              <button
                className="btn-previous"
                onClick={() => {
                  const currentIndex = WIZARD_STEPS.findIndex((s) => s.key === currentStep);
                  if (currentIndex > 0) {
                    handleStepChange(WIZARD_STEPS[currentIndex - 1].key);
                  }
                }}
              >
                <i className="bx bx-chevron-left"></i>
                Previous
              </button>
            )}
            {WIZARD_STEPS.findIndex((s) => s.key === currentStep) < WIZARD_STEPS.length - 1 && (
              <button
                className="btn-next"
                onClick={() => {
                  const currentIndex = WIZARD_STEPS.findIndex((s) => s.key === currentStep);
                  if (currentIndex < WIZARD_STEPS.length - 1) {
                    handleStepChange(WIZARD_STEPS[currentIndex + 1].key);
                  }
                }}
              >
                Next
                <i className="bx bx-chevron-right"></i>
              </button>
            )}
            {allStepsCompleted && (
              <button className="btn-complete" onClick={handleWizardComplete}>
                <i className="bx bx-check"></i>
                Complete Onboarding
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
